/** The pieces of the supplier exchange that more than one screen shows - **EXP-14**.
 *
 * `Suppliers.tsx` is the exchange; `SupplierAccount.tsx` is one supplier's account page. Both show
 * an answer with the division behind it, an agreement with its permitted uses, and the
 * responsiveness counts. They live here **once** because a fact rendered by two screens in two
 * ways is the thing `30-design-principles.md` rule 7 forbids: the second rendering is the one
 * nobody updates when the first changes.
 */

import { useState } from "react";

import { WriteFailed } from "../components/Loaded";
import { useReader } from "../api/profile";
import type { Agreement, Exchange } from "../api/suppliers";
import { useRevokeAgreement } from "../api/suppliers";
import type { SupplierResponsiveness } from "../api/supplierResponsiveness";
import { instantDate, shortDate, statedQuantity, unitLabel } from "../lib/format";
import styles from "./Screen.module.css";
import own from "./Suppliers.module.css";

function cls(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(" ");
}


export function Answer({ answer }: { answer: Exchange["submissions"][number] }) {
  return (
    <span className={cls(own.answer)} data-testid="answer">
      <span className={cls(own.figure)}>
        {statedQuantity(answer.quantity)} {unitLabel(answer.unit)}
      </span>
      {/* **The only evidence this figure has**, so it is printed beside it rather than behind a
          disclosure triangle. An assurance provider reads this first. */}
      <span className={cls(own.basis)}>{answer.stated_basis}</span>
      {answer.allocation ? <Allocation allocation={answer.allocation} unit={unitLabel(answer.unit)} /> : null}
    </span>
  );
}

/** The division behind a share — **S3-02**.
 *
 * *"Supplier inventory is allocated to purchased activity under a documented basis."* A share with
 * only its result is the arithmetic hidden one layer down, which is the defect EXP-36 recorded for
 * calendar-day apportionment: the figure was *resolvable* and not *complete*.
 *
 * **The two basis numbers, not a percentage.** `1,200 of 12,000` is the division a reader can redo;
 * `10%` is a rounding of it. The same choice the apportionment panel makes about days.
 *
 * Shown only where there is one: a direct answer — a supplier who metered the buyer's own delivery
 * point — has nothing to divide and is the better answer when it is available.
 */
function Allocation({
  allocation,
  unit,
}: {
  allocation: NonNullable<Exchange["submissions"][number]["allocation"]>;
  unit: string;
}) {
  return (
    <span className={cls(own.allocation)} data-testid="allocation">
      <span className={cls(own.sum)}>
        {statedQuantity(allocation.supplier_total)} {unit} total ·{" "}
        {statedQuantity(allocation.numerator)} of {statedQuantity(allocation.denominator)}{" "}
        {allocation.basis.replace(/_/g, " ")}
      </span>
      {/* Whose basis and over what period. The name alone is not a documented basis — two
          suppliers both saying "by volume" may have measured different things. */}
      <span className={cls(own.basis)}>{allocation.note}</span>
    </span>
  );
}

export function AgreementCard({ row, mayDecide }: { row: Agreement; mayDecide: boolean | undefined }) {
  const reader = useReader();
  const revoke = useRevokeAgreement(row.id);
  const [reason, setReason] = useState("");
  const [open, setOpen] = useState(false);
  const [refusal, setRefusal] = useState<unknown>(null);

  return (
    <li className={cls(own.agreement)} data-testid="supplier-agreement-row" data-agreement-id={row.id}>
      <span className={cls(own.meta)}>
        {row.i_am === "supplier" ? "You permit" : "Permitted to you"} ·{" "}
        {shortDate(row.effective_from)} –{" "}
        {row.expires_on ? shortDate(row.expires_on) : "no end date"}
      </span>
      <span className={cls(own.uses)}>
        {row.permitted_uses.map((use) => (
          <span key={use} className={cls(own.use)}>
            {use.replace(/_/g, " ")}
          </span>
        ))}
      </span>
      {row.revoked_at ? (
        <span className={cls(own.revoked)} data-testid="revoked">
          {/* **Revocation stops the next copy and un-makes nothing.** Said here so nobody goes
              looking for a figure that has quietly vanished — it has not. */}
          Revoked {instantDate(row.revoked_at, reader)} · {row.revoked_reason} · figures already
          copied under it stay in the inventory and keep citing it
        </span>
      ) : row.i_am === "supplier" && mayDecide !== false ? (
        <>
          <button
            type="button"
            className={styles.fixLink}
            data-testid="revoke-agreement"
            // "Keep it" **is** the cancel here, and it restores the prior state rather than only
            // closing the panel: a reason typed and abandoned must not be waiting the next time
            // somebody opens this, where it would read as a decision in progress.
            onClick={() => {
              setOpen((was) => !was);
              setReason("");
              setRefusal(null);
            }}
          >
            {open ? "Keep it" : "Revoke it"}
          </button>
          {open ? (
            <span className={cls(own.form)}>
              <label className={styles.field}>
                {/* Required: a permission withdrawn with no reason is one nobody can explain to
                    the customer whose next copy it refuses. */}
                <span>Why?</span>
                <input value={reason} onChange={(e) => setReason(e.target.value)} />
              </label>
              <button
                type="button"
                className={styles.primary}
                disabled={reason.trim().length < 3 || revoke.isPending}
                onClick={() => {
                  setRefusal(null);
                  revoke
                    .mutateAsync({ reason: reason.trim() })
                    .catch((failure: unknown) => setRefusal(failure));
                }}
              >
                Revoke
              </button>
            </span>
          ) : null}
          {/* A revocation whose outcome is unknown is **safe to re-send** in substance — revoking
              twice revokes once — but the server does not say so, so this does not claim it. The
              sentence says to look at the permission before deciding again. */}
          <WriteFailed error={refusal} of="this permission" />
        </>
      ) : null}
    </li>
  );
}

/** One supplier's responsiveness - ADR-0063 (and its 2026-10-25 clarifications).
 *
 * **"Performance" is responsiveness to requests and nothing else**, and the card says so rather
 * than using the word bare: no emissions trend, no data-quality score, no median below three
 * answers. Shared by the exchange screen's list and the supplier account's Performance tab, so the
 * two cannot describe one supplier differently.
 */
export function ResponsivenessCard({ row, heading }: { row: SupplierResponsiveness; heading?: React.ReactNode }) {
  return (
    <div className={own.responsiveness} data-supplier-id={row.supplier_org_id}>
        {heading ?? <h3 className={own.title}>Supplier <span>{row.supplier_org_id}</span></h3>}
        <dl className={own.responseMetrics}>
          <div><dt>Requests created</dt><dd data-testid="responsiveness-created">{row.created}</dd></div>
          <div><dt>Answered</dt><dd data-testid="responsiveness-answered">{row.answered}</dd></div>
          <div><dt>Outstanding (open)</dt><dd data-testid="responsiveness-outstanding">{row.outstanding}</dd></div>
          <div><dt>Declined</dt><dd>{row.declined}</dd></div>
          <div><dt>Withdrawn</dt><dd>{row.withdrawn}</dd></div>
          <div><dt>Median days to first answer</dt><dd data-testid="responsiveness-median">{row.timing_status === "ready" && row.median_days !== null ? `${row.median_days} days` : row.timing_status === "unknown" ? "Unknown: timing could not be established" : "Not shown: fewer than three answers"}</dd></div>
        </dl>
        <p className={styles.note}>Elapsed UTC days, one first answer per request. Invalid timing is unknown; a median is not computed from only the valid answers.</p>
        <details><summary>Request and answer sources</summary><ul className={own.list}>{row.requests.map((receipt) => <li key={receipt.request_id} className={own.responseSource} data-request-id={receipt.request_id}>
          <p>Request {receipt.request_id} · {receipt.state}</p>
          <p>Created: {receipt.created_at ?? "Time not recorded"}</p>
          <p>First answer: {receipt.first_answer_at ?? (receipt.timing_status === "no_answer" ? "No answer recorded" : "Timing unknown")}</p>
          <p>First answer IDs: {receipt.first_answer_ids.length ? receipt.first_answer_ids.join(", ") : "None established"}</p>
          <ul>{receipt.answers.map((answer) => <li key={answer.answer_id}>Answer {answer.answer_id} · {answer.submitted_at ?? "Time not recorded"}</li>)}</ul>
        </li>)}</ul></details>
      </div>
  );
}
